<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import Button from 'primevue/button'
import Card from 'primevue/card'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import Dropdown from 'primevue/dropdown'
import Tag from 'primevue/tag'
import { useAppToast } from '@/shared/lib/useAppToast'
import {
  getParsePreview, getParseStructure, queueImportParsing, uploadImportSource
} from '../api/imports.service'
import { enumLabel, formatDate, importStatusValues, parseStates, safeJson, stateSeverity } from '../lib/importDisplay'

const props = defineProps({ jobId: { type: String, required: true }, details: { type: Object, default: null } })
const emit = defineEmits(['changed', 'poll', 'structure'])
const toast = useAppToast()
const fileInput = ref(null)
const uploading = ref(false)
const queueing = ref(false)
const structureLoading = ref(false)
const previewLoading = ref(false)
const structure = ref(null)
const selectedDocumentId = ref(null)
const selectedSheetId = ref(null)
const preview = ref(null)
const previewElapsedMs = ref(null)

const documents = computed(() => (structure.value?.sources ?? []).flatMap(source =>
  (source.documents ?? []).map(document => ({
    ...document,
    sourceName: source.originalFileName,
    label: `${source.originalFileName} · ${document.originalRelativePath || document.displayName}`
  }))
))
const selectedDocument = computed(() => documents.value.find(x => x.id === selectedDocumentId.value) ?? null)
const sheets = computed(() => (selectedDocument.value?.sheets ?? []).map(sheet => ({
  ...sheet, label: `${sheet.sheetOrder + 1}. ${sheet.sheetName}${sheet.isHidden ? ' (скрыт)' : ''}`
})))
const selectedSheet = computed(() => sheets.value.find(x => x.id === selectedSheetId.value) ?? null)
const previewColumns = computed(() => {
  const indexes = new Set()
  for (const row of preview.value?.rows ?? []) for (const cell of row.cells ?? []) indexes.add(cell.columnIndex)
  return [...indexes].sort((a, b) => a - b)
})

function cellAt(row, index) {
  const cell = (row.cells ?? []).find(value => value.columnIndex === index)
  if (!cell) return '—'
  if (cell.kind === 'formula') return `=${cell.rawValue ?? ''}`
  return cell.normalizedText ?? cell.rawValue ?? '∅'
}

function columnsMetadata(sheet) {
  const value = safeJson(sheet?.columns, [])
  return Array.isArray(value) ? value : Object.entries(value).map(([key, entry]) => ({ key, ...entry }))
}

async function loadStructure() {
  if (!props.details?.job?.activeParseAttemptId) {
    structure.value = null
    return
  }
  structureLoading.value = true
  try {
    structure.value = await getParseStructure(props.jobId)
    emit('structure', structure.value)
    if (!documents.value.some(x => x.id === selectedDocumentId.value)) {
      selectedDocumentId.value = documents.value[0]?.id ?? null
    }
  } catch (error) {
    toast.error(error.message)
  } finally {
    structureLoading.value = false
  }
}

async function loadPreview() {
  if (!selectedDocumentId.value || !selectedSheetId.value) return
  previewLoading.value = true
  const started = performance.now()
  try {
    preview.value = await getParsePreview(props.jobId, selectedDocumentId.value, selectedSheetId.value, 100)
  } catch (error) {
    preview.value = null
    toast.error(error.message)
  } finally {
    previewElapsedMs.value = Math.round(performance.now() - started)
    previewLoading.value = false
  }
}

async function upload(event) {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (!file) return
  uploading.value = true
  try {
    await uploadImportSource(props.jobId, file)
    toast.success(`Файл ${file.name} загружен`)
    emit('changed')
  } catch (error) {
    toast.error(error.message)
  } finally {
    uploading.value = false
  }
}

async function queueParsing() {
  queueing.value = true
  try {
    await queueImportParsing(props.jobId)
    toast.success('Разбор поставлен в очередь')
    emit('changed')
    emit('poll')
  } catch (error) {
    toast.error(error.message)
  } finally {
    queueing.value = false
  }
}

watch(selectedDocumentId, () => {
  selectedSheetId.value = sheets.value[0]?.id ?? null
  preview.value = null
})
watch(() => props.details?.job?.activeParseAttemptId, loadStructure)
onMounted(loadStructure)
defineExpose({ loadStructure })
</script>

<template>
  <div class="panel-stack">
    <Card>
      <template #title>Источники и разбор</template>
      <template #content>
        <div class="toolbar">
          <div class="source-list">
            <div v-for="source in details?.sources ?? []" :key="source.id" class="source-row">
              <i class="pi pi-file"></i>
              <div><strong>{{ source.originalFileName }}</strong><small>{{ source.sizeBytes }} байт · {{ formatDate(source.uploadedAt) }}</small></div>
            </div>
            <div v-if="!(details?.sources?.length)" class="muted">Файлы ещё не загружены.</div>
          </div>
          <div class="toolbar-actions">
            <input ref="fileInput" type="file" accept=".csv,.xlsx,.zip" hidden @change="upload" />
            <Button label="Загрузить файл" icon="pi pi-upload" outlined :loading="uploading" @click="fileInput.click()" />
            <Button label="Запустить разбор" icon="pi pi-play" :loading="queueing"
              :disabled="!(details?.sources?.length) || [importStatusValues.QueuedForParsing, importStatusValues.Parsing, importStatusValues.ReadyForReview].includes(details?.job?.status)" @click="queueParsing" />
          </div>
        </div>

        <div v-if="details?.activeParseAttempt" class="attempt-summary">
          <Tag :value="enumLabel(parseStates, details.activeParseAttempt.state)"
            :severity="stateSeverity(details.activeParseAttempt.state, 'parse')" />
          <span>Попытка {{ details.activeParseAttempt.attemptNumber }}</span>
          <span>{{ details.activeParseAttempt.parserVersion || 'Адаптер определяется' }}</span>
          <span>{{ details.activeParseAttempt.processedRows }} строк</span>
          <span v-if="details.activeParseAttempt.failureCode" class="danger">{{ details.activeParseAttempt.failureCode }}</span>
        </div>
      </template>
    </Card>

    <Card v-if="details?.job?.activeParseAttemptId">
      <template #title>Структура и raw preview</template>
      <template #content>
        <div class="selectors">
          <label><span>Документ</span><Dropdown v-model="selectedDocumentId" :options="documents" option-label="label" option-value="id" /></label>
          <label><span>Лист</span><Dropdown v-model="selectedSheetId" :options="sheets" option-label="label" option-value="id" /></label>
          <Button label="Показать до 100 строк" icon="pi pi-table" :loading="previewLoading" :disabled="!selectedSheetId" @click="loadPreview" />
        </div>

        <div v-if="selectedSheet" class="sheet-meta">
          <span>Строк: <strong>{{ selectedSheet.rowCount }}</strong></span>
          <span>Колонок: <strong>{{ selectedSheet.columnCount }}</strong></span>
          <span>Chunks: <strong>{{ selectedSheet.chunkCount }}</strong></span>
          <span>Header row: <strong>{{ selectedSheet.headerRowNumber ?? 'не определён' }}</strong></span>
        </div>
        <div v-if="columnsMetadata(selectedSheet).length" class="column-pills">
          <span v-for="(column, index) in columnsMetadata(selectedSheet)" :key="index">
            {{ column.header ?? column.name ?? column.key ?? `Колонка ${index + 1}` }}
          </span>
        </div>

        <DataTable v-if="preview" :value="preview.rows" :loading="previewLoading" scrollable scroll-height="430px" class="preview-table">
          <Column field="rowNumber" header="#" frozen />
          <Column v-for="index in previewColumns" :key="index" :header="`Колонка ${index + 1}`">
            <template #body="{ data }"><span class="cell-value">{{ cellAt(data, index) }}</span></template>
          </Column>
          <template #footer>Получено {{ preview.rows.length }} строк · {{ previewElapsedMs }} ms · формулы отображаются только как текст</template>
        </DataTable>
        <div v-else-if="structureLoading" class="muted">Загрузка структуры…</div>
      </template>
    </Card>
  </div>
</template>

<style scoped>
.panel-stack { display: grid; gap: 16px; }
.toolbar { display: flex; justify-content: space-between; gap: 20px; align-items: flex-start; }
.source-list { display: grid; gap: 8px; min-width: 260px; }
.source-row { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border: 1px solid #e8e8e5; border-radius: 12px; }
.source-row i { color: #b3171d; }
.source-row div { display: grid; gap: 2px; }
.source-row small, .muted { color: #777; font-size: 12px; }
.toolbar-actions { display: flex; gap: 8px; flex-wrap: wrap; justify-content: flex-end; }
.attempt-summary, .sheet-meta { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; margin-top: 16px; color: #606060; font-size: 13px; }
.selectors { display: grid; grid-template-columns: minmax(220px, 1fr) minmax(180px, 1fr) auto; gap: 12px; align-items: end; }
.selectors label { display: grid; gap: 6px; font-size: 12px; font-weight: 600; }
.column-pills { display: flex; gap: 6px; flex-wrap: wrap; margin: 14px 0; }
.column-pills span { padding: 4px 8px; border-radius: 999px; background: #f1f1ef; font-size: 11px; }
.preview-table { margin-top: 16px; }
.cell-value { display: block; min-width: 110px; max-width: 280px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.danger { color: #b42318; }
@media (max-width: 760px) { .toolbar { flex-direction: column; } .selectors { grid-template-columns: 1fr; } }
</style>
